/*
  Tri-Art colour drawdown and mixer.
  - Inline card: the product's technical drawdown (tints, masstone, gel dilutions over white and black).
  - Pop-up mixer: opened from the card or from the "Mix this colour" button added to the product gallery.
  Colour model: two-constant Kubelka-Munk. Each colour's absorption curve (K, 38 bands), scattering (s)
  and drawdown film thicknesses were fitted to Tri-Art drawdown photographs with QC corrections.
  Paints mix by volume. spectral.js (MIT) converts reflectance to sRGB.
*/
(() => {
  if (window.TACM) return;
  const NB = 38, RG_W = 0.82, RG_K = 0.01, MAX = 4, MAXP = 20;
  const STRIPES = [{ tint: 1 / 11 }, { tint: 0.5 }, { conc: 1 }, { conc: 0.5 }, { conc: 1 / 11 }];
  const OPQ = { T: 0.25, SO: 0.65, O: 1 }, GLOSS = { G: 1, SG: 0.55, M: 0.15 };
  const GROUP_ORDER = ["Yellows", "Oranges", "Reds", "Pinks and violets", "Blues", "Greens", "Earths", "Neutrals"];
  /* starter mixes for the full-page mixer when nobody arrives with a colour */
  const STARTERS = [
    [["phthalo-blue-green-shade", 1], ["primary-yellow", 2]],
    [["quinacridone-magenta", 1], ["pyrrole-orange", 2]],
    [["cadmium-yellow-medium-hue", 2], ["pyrrole-orange", 1]],
    [["phthalo-turquoise", 1], ["arylide-yellow-light", 3]],
    [["ultramarine-blue-classic", 1], ["quinacridone-magenta", 1]],
    [["permanent-green-light", 1], ["yellow-oxide", 1]],
    [["burnt-sienna", 1], ["quinacridone-red", 1]],
    [["indian-yellow", 2], ["transparent-red-oxide", 1]],
    [["ultramarine-blue-classic", 1], ["burnt-sienna", 1]]
  ];
  /* popular mix recipes (names come from the section's translations) */
  const RECIPES = [
    { id: "paynes", g: "grey", m: [["ultramarine-blue-classic", 4], ["burnt-sienna", 1]] },
    { id: "chromatic_black", g: "grey", m: [["phthalo-green-blue-shade", 1], ["quinacridone-red", 1]] },
    { id: "warm_black", g: "grey", m: [["ultramarine-blue-classic", 1], ["burnt-umber", 2]] },
    { id: "warm_grey", g: "grey", m: [["ultramarine-blue-classic", 1], ["burnt-sienna", 1], ["titanium-white", 4]] },
    { id: "cool_grey", g: "grey", m: [["ultramarine-blue-classic", 4], ["burnt-sienna", 1], ["titanium-white", 6]] },
    { id: "sap_green", g: "green", m: [["phthalo-green-blue-shade", 1], ["burnt-sienna", 2], ["primary-yellow", 2]] },
    { id: "olive", g: "green", m: [["sap-green", 2], ["burnt-umber", 1], ["yellow-oxide", 2]] },
    { id: "forest", g: "green", m: [["phthalo-green-blue-shade", 2], ["burnt-sienna", 1], ["primary-yellow", 1]] },
    { id: "sage", g: "green", m: [["permanent-green-light", 1], ["burnt-umber", 1], ["titanium-white", 3]] },
    { id: "lime", g: "green", m: [["arylide-yellow-light", 5], ["phthalo-green-blue-shade", 1]] },
    { id: "emerald", g: "green", m: [["phthalo-green-blue-shade", 1], ["primary-yellow", 1]] },
    { id: "navy", g: "blue", m: [["ultramarine-blue-classic", 4], ["phthalo-blue-red-shade", 1], ["burnt-umber", 1]] },
    { id: "teal", g: "blue", m: [["phthalo-blue-green-shade", 1], ["phthalo-green-blue-shade", 1], ["primary-yellow", 1]] },
    { id: "turquoise", g: "blue", m: [["phthalo-blue-green-shade", 1], ["permanent-green-light", 1], ["titanium-white", 2]] },
    { id: "sky", g: "blue", m: [["cerulean-blue-hue", 1], ["titanium-white", 3]] },
    { id: "denim", g: "blue", m: [["ultramarine-blue-classic", 2], ["paynes-grey", 1], ["titanium-white", 3]] },
    { id: "purple", g: "purple", m: [["ultramarine-blue-classic", 1], ["quinacridone-magenta", 1]] },
    { id: "plum", g: "purple", m: [["quinacridone-violet", 2], ["ultramarine-blue-classic", 1]] },
    { id: "lavender", g: "purple", m: [["dioxazine-violet", 1], ["titanium-white", 8]] },
    { id: "mauve", g: "purple", m: [["quinacridone-magenta", 2], ["ultramarine-blue-classic", 1], ["burnt-umber", 1], ["titanium-white", 4]] },
    { id: "crimson", g: "red", m: [["quinacridone-magenta", 1], ["pyrrole-red-medium", 2]] },
    { id: "burgundy", g: "red", m: [["quinacridone-red", 3], ["burnt-umber", 1]] },
    { id: "coral", g: "red", m: [["pyrrole-orange", 2], ["quinacridone-magenta", 1], ["titanium-white", 2]] },
    { id: "dusty_rose", g: "red", m: [["quinacridone-red", 1], ["burnt-sienna", 1], ["titanium-white", 4]] },
    { id: "burnt_orange", g: "orange", m: [["pyrrole-orange", 2], ["burnt-sienna", 1]] },
    { id: "terracotta", g: "orange", m: [["red-oxide", 2], ["pyrrole-orange", 1], ["titanium-white", 1]] },
    { id: "peach", g: "orange", m: [["pyrrole-orange", 1], ["naples-yellow-hue", 2], ["titanium-white", 3]] },
    { id: "mustard", g: "orange", m: [["arylide-yellow-deep", 3], ["raw-umber", 1]] },
    { id: "amber", g: "orange", m: [["indian-yellow", 2], ["transparent-red-oxide", 1]] },
    { id: "chocolate", g: "brown", m: [["burnt-umber", 2], ["pyrrole-red-medium", 1]] },
    { id: "primary_brown", g: "brown", m: [["pyrrole-red-medium", 1], ["primary-yellow", 1], ["ultramarine-blue-classic", 1]] },
    { id: "tan", g: "brown", m: [["burnt-sienna", 1], ["yellow-oxide", 1], ["titanium-white", 3]] }
  ];
  /* stand-ins when a recipe colour isn't made in the current paint line */
  const RECIPE_SUBS = { "phthalo-blue-red-shade": ["prussian-blue-hue", "phthalo-blue-green-shade"], "red-oxide": ["burnt-sienna"], "transparent-red-oxide": ["burnt-sienna"] };
  const RECIPE_GROUPS = ["grey", "green", "blue", "purple", "red", "orange", "brown"];
  const HARMONIES = { complementary: [180], split: [150, 210], analogous: [-30, 30], triadic: [120, 240], square: [90, 180, 270] };
  const PREVIEW = window.TACM_PREVIEW || null;
  const reduceMotion = () => !!(window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches);
  const isWide = () => !!(window.matchMedia && matchMedia("(min-width: 900px)").matches);

  /* ---------- loading ---------- */
  let specP = null, dataP = null;
  const loadScript = src => new Promise((res, rej) => { const s = document.createElement("script"); s.src = src; s.async = true; s.onload = res; s.onerror = () => rej(new Error("not found")); document.head.appendChild(s); });
  const loadSpectral = src => specP || (specP = window.spectral ? Promise.resolve() : loadScript(src));
  const loadData = url => dataP || (dataP = PREVIEW && PREVIEW.data ? Promise.resolve(PREVIEW.data)
    : window.TACM_DATA ? Promise.resolve(window.TACM_DATA)
    : /\.js(\?|$)/.test(url) ? loadScript(url).then(() => { if (!window.TACM_DATA) throw new Error("no colour data in file"); return window.TACM_DATA; })
    : fetch(url).then(r => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); }));

  /* ---------- helpers ---------- */
  const fill = (s, o) => String(s).replace(/\{(\w+)\}/g, (m, k) => (k in o ? o[k] : m));
  const esc = s => String(s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const hexToRgb = h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
  const wrap = a => ((a % 360) + 360) % 360;
  const angDist = (a, b) => { const d = Math.abs(wrap(a) - wrap(b)); return d > 180 ? 360 - d : d; };
  function film(K, S, X, Rg) {
    const R = new Array(NB);
    for (let i = 0; i < NB; i++) {
      const s = Math.max(S, 1e-6), a = 1 + Math.max(K[i], 1e-7) / s, b = Math.sqrt(a * a - 1), t = b * s * X;
      if (t > 30) { R[i] = a - b; continue; }
      const ct = 1 / Math.tanh(t);
      R[i] = (1 - Rg * (a - b * ct)) / (a - Rg + b * ct);
    }
    return R;
  }
  const hexOfR = R => new window.spectral.Color(R.map(r => Math.min(1, Math.max(1e-6, r)))).toString().toLowerCase();
  function oklchToHex(L, C, h) {
    for (let k = 0; k < 30; k++, C *= 0.92) {
      const a = C * Math.cos(h * Math.PI / 180), b = C * Math.sin(h * Math.PI / 180);
      const l_ = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3, m_ = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3, s_ = (L - 0.0894841775 * a - 1.2914855480 * b) ** 3;
      const rgb = [4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_, -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_, -0.0041960863 * l_ - 0.7034186147 * m_ + 1.7076147010 * s_];
      if (rgb.every(v => v >= -0.0005 && v <= 1.0005) || k === 29) return "#" + rgb.map(v => { v = Math.min(1, Math.max(0, v)); v = v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055; return Math.round(v * 255).toString(16).padStart(2, "0"); }).join("");
    }
  }
  /* painter's colour wheel: red opposite green, yellow opposite violet, blue opposite orange (hue of each colour's 1:1 tint) */
  const ANCH = [[25, 0], [50, 60], [98, 120], [150, 180], [245, 240], [295, 300], [350, 330], [385, 360]];
  function okToWheel(h) { h = wrap(h); if (h < 25) h += 360; for (let i = 0; i < ANCH.length - 1; i++) { const [a0, w0] = ANCH[i], [a1, w1] = ANCH[i + 1]; if (h <= a1) return wrap(w0 + (h - a0) / (a1 - a0) * (w1 - w0)); } return 0; }
  function wheelToOk(w) { w = wrap(w); for (let i = 0; i < ANCH.length - 1; i++) { const [a0, w0] = ANCH[i], [a1, w1] = ANCH[i + 1]; if (w <= w1) return wrap(a0 + (w - w0) / (w1 - w0) * (a1 - a0)); } return 25; }
  function rng(seed) { seed = Math.imul(seed ^ 0x9e3779b9, 2654435761) >>> 0; for (let i = 0; i < 5; i++) seed = (seed * 1664525 + 1013904223) >>> 0; return () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; }; }
  function noise1(n) { const r = rng(n); const pts = Array.from({ length: 64 }, r); return x => { x = ((x % 63) + 63) % 63; const i = Math.floor(x), f = x - i, u = f * f * (3 - 2 * f); return pts[i] * (1 - u) + pts[i + 1] * u; }; }
  /* Shopify's t filter HTML-escapes translations (painter&#39;s); decode, because they are inserted as text */
  function decodeStrings(json) {
    const ta = document.createElement("textarea"), dec = s => { ta.innerHTML = s; return ta.value; };
    const deep = o => typeof o === "string" ? dec(o) : Array.isArray(o) ? o.map(deep) : o && typeof o === "object" ? Object.fromEntries(Object.entries(o).map(([k, v]) => [k, deep(v)])) : o;
    return deep(JSON.parse(json));
  }

  /* small colour-wheel symbol for each harmony: the mix at the top, the suggestions around the ring */
  function harmIcon(k) {
    const pt = a => [14 + 9 * Math.sin(a * Math.PI / 180), 14 - 9 * Math.cos(a * Math.PI / 180)].map(v => v.toFixed(1));
    const angs = [0].concat(k === "analogous" ? [-50, 50] : HARMONIES[k]), pts = angs.map(pt);
    const arc = k === "analogous" ? (() => { const a0 = pt(-50), a1 = pt(50); return `<path d="M${a0[0]} ${a0[1]}A9 9 0 0 1 ${a1[0]} ${a1[1]}" stroke-width="3"/>`; })() : "";
    const shape = arc || (pts.length === 2 ? `<line x1="${pts[0][0]}" y1="${pts[0][1]}" x2="${pts[1][0]}" y2="${pts[1][1]}"/>` : `<polygon points="${pts.map(p => p.join(",")).join(" ")}"/>`);
    return `<svg class="tacm-dlg__hicon" viewBox="0 0 28 28" width="28" height="28" aria-hidden="true"><circle cx="14" cy="14" r="9" fill="none" stroke="currentColor" stroke-opacity=".3" stroke-width="2"/>
      <g fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="${pts.length === 2 ? "2.5 2" : "0"}">${shape}</g>
      ${pts.map((p, i) => `<circle cx="${p[0]}" cy="${p[1]}" r="${i ? 2.6 : 3.2}" fill="${i ? "currentColor" : "#E4002B"}"/>`).join("")}</svg>`;
  }

  /* ---------- colour model ---------- */
  class Model {
    constructor(data) {
      data.colours.forEach(c => { if (!c.L && c.h) c.L = { hv: [c.h, c.v] }; });
      this.colours = data.colours;
      this.byId = Object.fromEntries(data.colours.map(c => [c.id, c]));
      this.white = this.byId["titanium-white"];
    }
    find(handle) { for (const c of this.colours) for (const k in c.L) if (c.L[k][0] === handle) return { c, line: k }; return null; }
    mixKS(items) {
      const K = new Array(NB).fill(0); let S = 0, V = 0, X = 0, G1 = 0, G10 = 0;
      items.forEach(m => V += m.parts);
      for (const m of items) {
        const c = this.byId[m.id], w = m.parts / V;
        for (let i = 0; i < NB; i++) K[i] += c.K[i] * w;
        S += c.s * w; X += c.x * w; G1 += c.g1 * w; G10 += c.g10 * w;
      }
      return { K, S, X, G1, G10 };
    }
    stripeKS(b, st) {
      if (st.tint) { const w = this.white, c = st.tint; return { K: b.K.map((k, i) => c * k + (1 - c) * w.K[i]), S: c * b.S + (1 - c) * w.s, X: 1e3 }; }
      const g = st.conc === 1 ? 1 : st.conc > 0.2 ? b.G1 : b.G10;
      return { K: b.K.map(k => k * st.conc), S: b.S * st.conc, X: b.X * g };
    }
    summary(items) {
      const b = this.mixKS(items), P = items.reduce((a, m) => a + m.parts, 0);
      const mass = hexOfR(film(b.K, b.S, b.X, RG_W));
      const t10 = this.stripeKS(b, STRIPES[4]), thin = hexOfR(film(t10.K, t10.S, t10.X, RG_W));
      let op = 0, gl = 0;
      for (const m of items) { const c = this.byId[m.id], w = m.parts / P; op += OPQ[c.op] * w; gl += GLOSS[c.sh] * w; }
      return { b, mass, thin, op: op < 0.42 ? "T" : op < 0.82 ? "SO" : "O", gloss: gl, sh: gl > 0.78 ? "G" : gl > 0.38 ? "SG" : "M" };
    }
    tintLch(K, S) {
      const w = this.white, Km = K.map((k, i) => 0.5 * k + 0.5 * w.K[i]);
      const o = new window.spectral.Color(film(Km, 0.5 * S + 0.5 * w.s, 1e3, 0).map(r => Math.min(1, Math.max(1e-6, r)))).OKLab;
      return { L: o[0], C: Math.hypot(o[1], o[2]), h: wrap(Math.atan2(o[2], o[1]) * 180 / Math.PI) };
    }
    ident(c) { if (!c._id) { const t = this.tintLch(c.K, c.s); c._id = { ...t, w: okToWheel(t.h) }; } return c._id; }
    mixIdent(items) { const b = this.mixKS(items), t = this.tintLch(b.K, b.S); return { ...t, w: okToWheel(t.h) }; }
    isChromatic(c) { return c.g !== "Neutrals" && this.ident(c).C >= 0.03; }
    matchTarget(list, target, used) {
      const pen = C => Math.max(0, 0.09 - C) * 150;
      const cands = list.filter(c => !used.has(c.id) && this.isChromatic(c));
      let best = null;
      for (const c of cands) { const id = this.ident(c), sc = angDist(id.w, target) + pen(id.C); if (!best || sc < best.score) best = { score: sc, items: [{ id: c.id, parts: 1 }], w: id.w }; }
      if (!best) return null;
      const near = cands.map(c => ({ c, d: angDist(this.ident(c).w, target) })).filter(x => x.d < 70).sort((a, b) => a.d - b.d).slice(0, 10);
      for (let i = 0; i < near.length; i++) for (let j = i + 1; j < near.length; j++) {
        const a = near[i].c, b = near[j].c, sa = Math.sign(((this.ident(a).w - target + 540) % 360) - 180), sb = Math.sign(((this.ident(b).w - target + 540) % 360) - 180);
        if (sa === sb) continue;
        for (const [pa, pb] of [[3, 1], [2, 1], [1, 1], [1, 2], [1, 3]]) {
          const wa = pa / (pa + pb), wb = 1 - wa;
          const t = this.tintLch(a.K.map((k, n) => k * wa + b.K[n] * wb), a.s * wa + b.s * wb), w = okToWheel(t.h);
          const sc = angDist(w, target) + pen(t.C) + 8;
          if (sc < best.score) best = { score: sc, items: [{ id: a.id, parts: pa }, { id: b.id, parts: pb }], w };
        }
      }
      best.items.forEach(m => used.add(m.id));
      return best;
    }
  }

  /* ---------- drawdown renderer (canvas, knife-ridge texture, tilt and finish) ---------- */
  class Drawdown {
    constructor(model, canvas, stage, card, opts = {}) {
      this.model = model; this.canvas = canvas; this.stage = stage; this.card = card; this.opts = opts;
      this.dd = { light: [-0.35, -0.5], target: [-0.35, -0.5], gloss: 1, raf: 0, levels: 28 };
      this.items = []; this.built = false;
      this.bindTilt();
    }
    build() {
      const cv = this.canvas, dpr = Math.min(2, window.devicePixelRatio || 1);
      const cw = cv.clientWidth || 600, ch = cv.clientHeight || cw * 9.5 / 16;
      const sc = Math.min(1, 1100 / (cw * dpr)), w = Math.max(320, Math.round(cw * dpr * sc)), h = Math.max(160, Math.round(ch * dpr * sc));
      cv.width = w; cv.height = h;
      const dd = this.dd, N = w * h; dd.W = w; dd.H = h; dd.cw = cw; dd.ch = ch;
      dd.stripe = new Int8Array(N).fill(-1); dd.sub = new Uint8Array(N); dd.th = new Float32Array(N);
      dd.nx = new Float32Array(N); dd.ny = new Float32Array(N); dd.nz = new Float32Array(N);
      const BAND = [0.46, 0.76], SY = [0.07, 0.93], M = 0.04, G = 0.016, SW = (1 - 2 * M - 4 * G) / 5, hm = new Float32Array(N);
      for (let s = 0; s < 5; s++) {
        const r = rng(17 + s * 101), nA = noise1(31 + s), nB = noise1(77 + s * 3), nC = noise1(5 + s * 13), nE = noise1(111 + s), nS = noise1(201 + s * 7), nT = noise1(301 + s * 5);
        const freqs = [6 + r() * 3, 15 + r() * 6, 34 + r() * 12, 70 + r() * 25], amps = [0.13, 0.085, 0.05, 0.03], ph = freqs.map(() => r() * 6.28);
        const x0 = (M + s * (SW + G)) * w, x1 = x0 + SW * w;
        for (let y = 0; y < h; y++) {
          const v = y / h, eL = 0.005 * w * (nE(v * 3) - 0.5) * 2, eR = 0.005 * w * (nE(v * 3 + 30) - 0.5) * 2;
          for (let x = Math.max(0, Math.floor(x0 - 0.02 * w)); x < Math.min(w, Math.ceil(x1 + 0.02 * w)); x++) {
            const left = x0 + eL, right = x1 + eR;
            if (x < left || x > right) continue;
            const u = (x - left) / (right - left);
            const top = SY[0] + 0.018 * (nA(u * 5) - 0.5) * 2, bot = SY[1] - 0.016 * (nB(u * 4) - 0.3) * 2;
            if (v < top || v > bot) continue;
            let hh = 0.5;
            for (let k = 0; k < 4; k++) hh += amps[k] * Math.sin(6.283 * freqs[k] * u + ph[k] + 0.6 * (nC(v * 0.7 + k * 9) - 0.5));
            hh += 0.10 * (nS(u * 60 + 0.3 * nT(v * 0.5)) - 0.5) + 0.06 * (nT(u * 140) - 0.5);
            hh += 0.2 * Math.exp(-(((u - 0.02) / 0.035) ** 2)) + 0.2 * Math.exp(-(((u - 0.98) / 0.035) ** 2));
            hh += 0.16 * Math.exp(-(((v - top) / 0.025) ** 2));
            const i = y * w + x;
            hm[i] = Math.min(1, Math.max(0.04, hh)); dd.stripe[i] = s; dd.sub[i] = v > BAND[0] && v < BAND[1] ? 1 : 0;
          }
        }
      }
      for (let i = 0; i < N; i++) if (dd.stripe[i] < 0) { const v = Math.floor(i / w) / h; dd.sub[i] = v > BAND[0] && v < BAND[1] ? 1 : 0; }
      const A = 0.9 * SW * w / 25;
      for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
        const i = y * w + x;
        if (dd.stripe[i] < 0) { dd.nz[i] = 1; continue; }
        const gx = (hm[i + 1] - hm[i - 1]) * A, gy = (hm[i + w] - hm[i - w]) * A, l = Math.hypot(gx, gy, 1);
        dd.nx[i] = -gx / l; dd.ny[i] = -gy / l; dd.nz[i] = 1 / l; dd.th[i] = hm[i];
      }
      dd.ctx = cv.getContext("2d"); dd.img = dd.ctx.createImageData(w, h);
      dd.card = [hexToRgb(hexOfR(new Array(NB).fill(RG_W))), hexToRgb(hexOfR(new Array(NB).fill(RG_K)))];
      this.built = true;
    }
    rebuildIfResized() {
      if (!this.built) return;
      const cw = this.canvas.clientWidth, ch = this.canvas.clientHeight;
      if (cw && (Math.abs(cw - this.dd.cw) > 40 || Math.abs(ch - this.dd.ch) > 30)) { this.build(); this.show(this.items); }
    }
    show(items) {
      this.items = items;
      if (!this.built) this.build();
      const dd = this.dd;
      if (!items.length) { dd.lut = null; this.schedule(); return null; }
      const r = this.model.summary(items), L = dd.levels, lut = new Uint8Array(5 * 2 * L * 3);
      for (let s = 0; s < 5; s++) {
        const st = this.model.stripeKS(r.b, STRIPES[s]);
        for (let k = 0; k < 2; k++) for (let l = 0; l < L; l++) {
          const X = st.X > 100 ? st.X : st.X * (0.5 + 1.0 * l / (L - 1));
          lut.set(hexToRgb(hexOfR(film(st.K, st.S, X, k ? RG_K : RG_W))), ((s * 2 + k) * L + l) * 3);
        }
      }
      dd.lut = lut; dd.gloss = r.gloss; this.schedule();
      return r;
    }
    paint() {
      const dd = this.dd; dd.raf = 0;
      if (!dd.img) return;
      const { W, H, card, levels: L } = dd, d = dd.img.data, lut = dd.lut;
      dd.light[0] += (dd.target[0] - dd.light[0]) * 0.35; dd.light[1] += (dd.target[1] - dd.light[1]) * 0.35;
      let lx = dd.light[0], ly = dd.light[1], lz = 1; const ll = Math.hypot(lx, ly, lz); lx /= ll; ly /= ll; lz /= ll;
      let hx = lx, hy = ly, hz = lz + 1; const hl = Math.hypot(hx, hy, hz); hx /= hl; hy /= hl; hz /= hl;
      const g = dd.gloss, shin = 5 + 55 * g * g, spec = 0.05 + 0.5 * g;
      for (let i = 0, p = 0; i < W * H; i++, p += 4) {
        const s = dd.stripe[i], k = dd.sub[i];
        if (s < 0 || !lut) { const c = card[k]; d[p] = c[0]; d[p + 1] = c[1]; d[p + 2] = c[2]; d[p + 3] = 255; continue; }
        const o = ((s * 2 + k) * L + Math.min(L - 1, Math.floor(dd.th[i] * L))) * 3;
        const nx = dd.nx[i], ny = dd.ny[i], nz = dd.nz[i];
        const ndl = Math.max(0, nx * lx + ny * ly + nz * lz), ndh = Math.max(0, nx * hx + ny * hy + nz * hz);
        const diff = 0.8 + 0.22 * ndl, sp = 255 * spec * Math.pow(ndh, shin);
        d[p] = lut[o] * diff + sp; d[p + 1] = lut[o + 1] * diff + sp; d[p + 2] = lut[o + 2] * diff + sp; d[p + 3] = 255;
      }
      dd.ctx.putImageData(dd.img, 0, 0);
      if (Math.abs(dd.target[0] - dd.light[0]) > 0.004 || Math.abs(dd.target[1] - dd.light[1]) > 0.004) this.schedule();
    }
    schedule() { if (!this.dd.raf) this.dd.raf = requestAnimationFrame(() => this.paint()); }
    bindTilt() {
      const stage = this.stage, card = this.card, tilt = this.opts.tilt !== false;
      const rest = () => { this.dd.target = [-0.35, -0.5]; card.classList.remove("is-live"); card.style.transform = ""; this.schedule(); };
      const move = e => {
        const r = stage.getBoundingClientRect(), px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        this.dd.target = [px * 1.6, py * 1.6];
        if (tilt && !reduceMotion()) { card.classList.add("is-live"); card.style.transform = `rotateY(${px * 12}deg) rotateX(${-py * 9}deg)`; }
        this.schedule();
      };
      stage.addEventListener("pointermove", move);
      stage.addEventListener("pointerdown", move);
      stage.addEventListener("pointerleave", rest);
      stage.addEventListener("pointerup", e => { if (e.pointerType !== "mouse") rest(); });
      stage.addEventListener("pointercancel", rest);
    }
  }

  /* ---------- the pop-up mixer ---------- */
  class Mixer {
    constructor(root, model, base, line, T, opts = {}) {
      this.root = root; this.model = model; this.base = base; this.line = line; this.T = T; this.opts = opts;
      this.fr = (root.dataset.lang || "en").toLowerCase().startsWith("fr");
      this.list = model.colours.filter(c => c.L[line]);
      this.byId = model.byId;
      this.mix = opts.mix ? opts.mix.filter(m => this.byId[m.id] && this.byId[m.id].L[line]) : base ? [{ id: base.id, parts: 1 }] : [];
      this.preview = null; this.tab = "mix"; this.filter = "all"; this.starter = !!opts.starter; this.note = null; this.sheetMode = "colours"; this.rfilter = "all";
      this.harmKind = HARMONIES[root.dataset.harmony] ? root.dataset.harmony : (root.dataset.harmony === "none" ? null : "complementary");
      this.variantCache = new Map();
      this.money = (() => { try { return new Intl.NumberFormat(root.dataset.numberLocale || undefined, { style: "currency", currency: root.dataset.currency || "CAD" }); } catch (e) { return { format: v => "$" + v.toFixed(2) }; } })();
    }
    name(c) { return this.fr && c.nf ? c.nf : c.n; }
    dab(c) { return `radial-gradient(circle at 38% 36%, ${c.mh} 0 34%, ${c.th} 78%)`; }
    $(s) { return this.dlg.querySelector(s); }
    $$(s) { return this.dlg.querySelectorAll(s); }

    build() {
      const T = this.T, inline = !!this.opts.inline, d = document.createElement(inline ? "div" : "dialog");
      d.className = "tacm-dlg" + (inline ? " is-inline" : ""); if (!inline) d.setAttribute("aria-labelledby", "tacm-dlg-title");
      const tabs = [["mix", T.tab_mix], ["harm", T.tab_harmonies], ["buy", T.tab_buy]];
      const lines = inline ? ["hv", "lq", "lv"].filter(k => this.model.colours.some(c => c.L[k])) : [];
      const xIcon = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" fill="none"/></svg>';
      d.innerHTML = `
        <div class="tacm-dlg__wrap">
          <div class="tacm-dlg__head">
            ${inline ? `<div class="tacm-dlg__lines" role="group" aria-label="${esc(T.line_label)}">${lines.map(k => `<button type="button" data-line="${k}" aria-pressed="${k === this.line}">${esc(T["line_" + k])}</button>`).join("")}</div>`
              : `<h2 class="tacm-dlg__title" id="tacm-dlg-title">${esc(T.dialog_title)}</h2><button type="button" class="tacm-dlg__x" data-close aria-label="${esc(T.close)}">${xIcon}</button>`}
          </div>
          <div class="tacm-dlg__body">
            <div class="tacm-dlg__visual">
              <div class="tacm-dlg__stage"><div class="tacm-dlg__card"><canvas class="tacm-dlg__canvas" role="img"></canvas></div>
                <p class="tacm-dlg__pv" hidden><span class="tacm-dlg__pv-text"></span><button type="button" data-unpreview>${esc(T.back)}</button></p></div>
              <div class="tacm-dlg__labels" aria-hidden="true">${T.stripes.map(s => `<span>${esc(s)}</span>`).join("")}</div>
              <div class="tacm-dlg__bar" aria-hidden="true"></div>
              <p class="tacm-dlg__meta" aria-live="polite"></p>
            </div>
            <div class="tacm-dlg__side">
              <div class="tacm-dlg__tabs">${tabs.map(([k, l]) => `<button type="button" data-tab="${k}" aria-pressed="${k === "mix"}">${esc(l)}</button>`).join("")}</div>
              <div class="tacm-dlg__panels">
                <div class="tacm-dlg__panel is-active" data-panel="mix">
                  <h3 class="tacm-dlg__h">${esc(T.mix_heading)}</h3>
                  <p class="tacm-dlg__starter" hidden>${esc(T.starter_note || "")}</p>
                  <ul class="tacm-dlg__recipe"></ul>
                  <div class="tacm-dlg__addrow">
                    <button type="button" class="tacm-dlg__addbtn" data-open-picker><span aria-hidden="true">+</span> ${esc(T.add_colour)}</button>
                    <button type="button" class="tacm-dlg__ideasbtn" data-open-recipes><span class="tacm-dlg__ideasdots" aria-hidden="true"><i style="background:#E4002B"></i><i style="background:#F7C600"></i><i style="background:#1F6FEB"></i></span>${esc(T.recipes_btn)}</button>
                  </div>
                  <button type="button" class="tacm-dlg__reset" data-reset hidden></button>
                  <div class="tacm-dlg__teaser" hidden></div>
                  ${!inline && this.root.dataset.pageUrl ? `<a class="tacm-dlg__full" data-full href="${esc(this.root.dataset.pageUrl)}">${esc(T.open_full)}</a>` : ""}
                </div>
                <div class="tacm-dlg__panel" data-panel="harm">
                  <h3 class="tacm-dlg__h">${esc(T.harmony_heading)}</h3>
                  <div class="tacm-dlg__chips" role="group" aria-label="${esc(T.harmony_heading)}">${Object.keys(HARMONIES).map(k => `<button type="button" class="tacm-dlg__chip" data-h="${k}" aria-pressed="false" aria-label="${esc(T.hnames[k])}" title="${esc(T.hnames[k])}">${harmIcon(k)}<span class="tacm-dlg__chip-text">${esc(T.hnames[k])}</span></button>`).join("")}</div>
                  <p class="tacm-dlg__desc"></p>
                  <div class="tacm-dlg__harm"></div>
                </div>
                <div class="tacm-dlg__panel" data-panel="buy">
                  <h3 class="tacm-dlg__h">${esc(T.tubes_heading)}</h3>
                  <div class="tacm-dlg__tubes"></div>
                  <p class="tacm-dlg__fine">${esc(T.disclaimer)}</p>
                </div>
              </div>
              <div class="tacm-dlg__foot">
                <div class="tacm-dlg__total"><span>${esc(T.total)}</span><strong></strong></div>
                <button type="button" class="tacm-dlg__cta tacm-dlg__cart">${esc(T.add_mix_cart)}</button>
                <p class="tacm-dlg__status" role="status"></p>
              </div>
            </div>
          </div>
          <div class="tacm-dlg__sheet" hidden>
            <div class="tacm-dlg__sheet-head"><h3 class="tacm-dlg__h tacm-dlg__sheet-title">${esc(T.add_colour)}</h3><button type="button" class="tacm-dlg__done" data-close-picker>${esc(T.done)}</button></div>
            <input type="search" class="tacm-dlg__find" autocomplete="off" placeholder="${esc(T.find)}" aria-label="${esc(T.find)}">
            <div class="tacm-dlg__filters"></div>
            <p class="tacm-dlg__note" aria-live="polite"></p>
            <div class="tacm-dlg__grid"></div>
          </div>
        </div>`;
      (inline ? this.opts.inline : document.body).appendChild(d);
      this.dlg = d;
      this.renderFilters();
      this.draw = new Drawdown(this.model, this.$(".tacm-dlg__canvas"), this.$(".tacm-dlg__stage"), this.$(".tacm-dlg__card"), { tilt: true });
      this.bind();
    }
    open() {
      if (!this.dlg) this.build();
      if (this.opts.inline) { requestAnimationFrame(() => { this.draw.rebuildIfResized(); this.render(); }); return; }
      if (this.dlg.open) return;
      this.lastFocus = document.activeElement;
      this.dlg.showModal();
      document.documentElement.classList.add("tacm-lock");
      try { history.pushState({ tacm: 1 }, ""); this.pushed = true; } catch (e) { this.pushed = false; }
      requestAnimationFrame(() => { this.draw.rebuildIfResized(); this.render(); });
    }
    close(fromHistory) {
      if (!this.dlg || this.opts.inline || !this.dlg.open) return;
      this.closePicker(true);
      this.dlg.close();
      document.documentElement.classList.remove("tacm-lock");
      if (!fromHistory && this.pushed && history.state && history.state.tacm) history.back();
      this.pushed = false;
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }
    bind() {
      const d = this.dlg;
      d.addEventListener("cancel", e => { e.preventDefault(); if (!this.$(".tacm-dlg__sheet").hidden) this.closePicker(); else this.close(); });
      d.addEventListener("keydown", e => { if (this.opts.inline && e.key === "Escape" && !this.$(".tacm-dlg__sheet").hidden) this.closePicker(); });
      const full = this.$("[data-full]");
      if (full) full.addEventListener("click", () => { try { const u = new URL(full.href, location.href); u.searchParams.set("line", this.line); u.searchParams.set("mix", this.mix.map(m => m.id + ":" + m.parts).join(",")); full.href = u.toString(); } catch (e) {} });
      d.addEventListener("click", e => {
        if (e.target === d) return this.close();
        const t = e.target.closest("button"); if (!t || t.disabled) return;
        if (t.matches("[data-close]")) return this.close();
        if (t.matches("[data-tab]")) return this.setTab(t.dataset.tab);
        if (t.matches("[data-line]")) return this.setLine(t.dataset.line);
        if (t.matches("[data-open-picker]")) return this.openPicker("colours");
        if (t.matches("[data-open-recipes]")) return this.openPicker("recipes");
        if (t.matches("[data-rfilter]")) { this.rfilter = t.dataset.rfilter; return this.renderPicker(); }
        if (t.matches("[data-recipe]")) {
          const r = this.recipesForLine().find(x => x.id === t.dataset.recipe); if (!r) return;
          this.mix = r.m.map(([id, parts]) => ({ id, parts })); this.starter = false;
          this.note = fill(this.T.recipe_note, { name: this.T.rnames[r.id] || r.id });
          this.preview = null; this.$(".tacm-dlg__pv").hidden = true; this.$(".tacm-dlg__status").textContent = "";
          this.render(); const again = this.$(`.tacm-dlg__grid [data-recipe="${r.id}"]`); if (again) again.focus(); return;
        }
        if (t.matches("[data-close-picker]")) return this.closePicker();
        if (t.matches("[data-unpreview]")) return this.setPreview(null);
        if (t.matches("[data-reset]")) { this.mix = this.base ? [{ id: this.base.id, parts: 1 }] : []; return this.changed(); }
        if (t.matches("[data-filter]")) { this.filter = t.dataset.filter; return this.renderPicker(); }
        if (t.matches("[data-d]")) { const k = +t.dataset.k; this.mix[k].parts = Math.max(1, Math.min(MAXP, this.mix[k].parts + +t.dataset.d)); this.changed(); const again = this.$(`.tacm-dlg__recipe button[data-k="${k}"][data-d="${t.dataset.d}"]`); if (again && !again.disabled) again.focus(); return; }
        if (t.matches("[data-rm]")) { this.mix.splice(+t.dataset.rm, 1); return this.changed(); }
        if (t.matches("[data-pick]")) {
          const id = t.dataset.pick, m = this.mix.find(x => x.id === id);
          if (m) m.parts = Math.min(MAXP, m.parts + 1); else if (this.mix.length < MAX) this.mix.push({ id, parts: 1 });
          this.changed(); const again = this.$(`.tacm-dlg__grid [data-pick="${id}"]`); if (again) again.focus(); return;
        }
        if (t.matches("[data-unpick]")) {
          const id = t.dataset.unpick, k = this.mix.findIndex(x => x.id === id);
          if (k >= 0) { if (this.mix[k].parts > 1) this.mix[k].parts--; else this.mix.splice(k, 1); }
          this.changed(); const again = this.$(`.tacm-dlg__grid [data-unpick="${id}"]`) || this.$(`.tacm-dlg__grid [data-pick="${id}"]`); if (again) again.focus(); return;
        }
        if (t.matches("[data-see-harm]")) return this.setTab("harm");
        if (t.matches(".tacm-dlg__chip")) { this.harmKind = this.harmKind === t.dataset.h ? null : t.dataset.h; return this.renderHarmony(); }
        if (t.matches("[data-view]")) return this.setPreview(this.harmSlots[+t.dataset.view].items);
        if (t.matches("[data-addmix]")) {
          const s = this.harmSlots[+t.dataset.addmix], P = this.mix.reduce((a, m) => a + m.parts, 0), H = s.items.reduce((a, m) => a + m.parts, 0);
          if (this.mix.length + s.items.length > MAX) { const ps = this.$(".tacm-dlg__pstatus"); if (ps) ps.textContent = this.T.full; return; }
          s.items.forEach(m => this.mix.push({ id: m.id, parts: Math.max(1, Math.min(MAXP, Math.round(m.parts * P / H))) }));
          return this.changed();
        }
        if (t.matches(".tacm-dlg__palette-cart")) return this.addToCart(this.harmIds, t, this.$(".tacm-dlg__pstatus"));
        if (t.matches(".tacm-dlg__cart")) return this.addToCart(this.mix.map(m => m.id), t, this.$(".tacm-dlg__status"));
      });
      this.$(".tacm-dlg__find").addEventListener("input", () => this.renderPicker());
      if (!this.opts.inline) window.addEventListener("popstate", () => { if (this.dlg.open) { this.pushed = false; this.close(true); } });
      let rT; window.addEventListener("resize", () => { clearTimeout(rT); rT = setTimeout(() => { if (this.opts.inline || this.dlg.open) this.draw.rebuildIfResized(); }, 250); });
    }
    recipesForLine() {
      this.rcache = this.rcache || {};
      if (!this.rcache[this.line]) {
        const has = id => this.byId[id] && this.byId[id].L[this.line];
        this.rcache[this.line] = RECIPES.map(r => {
          const m = r.m.map(([id, p]) => [has(id) ? id : (RECIPE_SUBS[id] || []).find(has), p]);
          return m.every(([id]) => id) ? { id: r.id, g: r.g, m } : null;
        }).filter(Boolean);
      }
      return this.rcache[this.line];
    }
    renderFilters() {
      const T = this.T;
      if (this.sheetMode === "recipes") {
        const rs = this.recipesForLine(), groups = ["all"].concat(RECIPE_GROUPS.filter(g => rs.some(r => r.g === g)));
        if (!groups.includes(this.rfilter)) this.rfilter = "all";
        this.$(".tacm-dlg__filters").innerHTML = groups.map(g => `<button type="button" data-rfilter="${g}" aria-pressed="${g === this.rfilter}">${esc(g === "all" ? T.all : (T.rgroups[g] || g))}</button>`).join("");
        return;
      }
      const groups = ["all"].concat(GROUP_ORDER.filter(g => this.list.some(c => c.g === g)));
      if (!groups.includes(this.filter)) this.filter = "all";
      this.$(".tacm-dlg__filters").innerHTML = groups.map(g => `<button type="button" data-filter="${esc(g)}" aria-pressed="${g === this.filter}">${esc(g === "all" ? T.all : (T.groups[g] || g))}</button>`).join("");
    }
    setLine(k) {
      if (k === this.line) return;
      this.line = k; this.list = this.model.colours.filter(c => c.L[k]); this.variantCache = new Map();
      this.mix = this.mix.filter(m => this.byId[m.id].L[k]);
      this.$$("[data-line]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.line === k)));
      this.renderFilters(); this.changed();
      if (!this.$(".tacm-dlg__sheet").hidden) this.renderPicker();
    }
    syncUrl() {
      if (!this.opts.inline) return;
      try {
        const u = new URL(location.href); u.searchParams.set("line", this.line);
        if (this.mix.length) u.searchParams.set("mix", this.mix.map(m => m.id + ":" + m.parts).join(",")); else u.searchParams.delete("mix");
        history.replaceState(history.state, "", u.pathname + u.search + u.hash);
      } catch (e) {}
    }
    setTab(k) {
      this.tab = k;
      this.$$(".tacm-dlg__tabs [data-tab]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.tab === k)));
      this.$$(".tacm-dlg__panel").forEach(p => p.classList.toggle("is-active", p.dataset.panel === k));
      this.$(".tacm-dlg__panels").scrollTop = 0;
      if (k === "harm") this.renderHarmony();
    }
    openPicker(mode) {
      this.sheetMode = mode === "recipes" ? "recipes" : "colours";
      this.$(".tacm-dlg__sheet-title").textContent = this.sheetMode === "recipes" ? this.T.recipes_title : this.T.add_colour;
      this.$(".tacm-dlg__find").value = "";
      this.$(".tacm-dlg__sheet").classList.toggle("is-recipes", this.sheetMode === "recipes");
      const s = this.$(".tacm-dlg__sheet"); s.hidden = false; this.renderFilters(); this.renderPicker();
      requestAnimationFrame(() => s.classList.add("is-open"));
      if (isWide()) this.$(".tacm-dlg__find").focus(); else this.$(".tacm-dlg__done").focus();
    }
    closePicker(silent) {
      const s = this.$(".tacm-dlg__sheet"); if (!s || s.hidden) return;
      s.classList.remove("is-open"); s.hidden = true;
      if (!silent) { const b = this.$(this.sheetMode === "recipes" ? "[data-open-recipes]" : "[data-open-picker]"); if (b) b.focus(); }
    }
    changed() { this.starter = false; this.note = null; this.preview = null; this.$(".tacm-dlg__pv").hidden = true; this.$(".tacm-dlg__status").textContent = ""; this.render(); }

    render() {
      this.renderDrawdown(); this.renderRecipe(); this.renderBar();
      if (!this.$(".tacm-dlg__sheet").hidden) this.renderPicker();
      this.renderTubes(); clearTimeout(this.hT); this.hT = setTimeout(() => this.renderHarmony(), 120);
      this.syncUrl();
    }
    renderDrawdown() {
      const T = this.T, items = this.preview || this.mix, r = this.draw.show(items);
      if (r) {
        this.$(".tacm-dlg__canvas").setAttribute("aria-label", fill(T.canvas_label, { name: items.map(m => this.name(this.byId[m.id])).join(" + "), thick: r.mass }));
        this.$(".tacm-dlg__meta").innerHTML = fill(esc(T.meta), { thick: `<strong>${r.mass.toUpperCase()}</strong>`, thin: `<strong>${r.thin.toUpperCase()}</strong>`, opacity: esc(T.opacity[r.op]), sheen: esc(T.sheen[r.sh]) });
      } else this.$(".tacm-dlg__meta").textContent = T.empty;
    }
    renderBar() {
      const bar = this.$(".tacm-dlg__bar");
      bar.innerHTML = this.mix.map(m => `<span style="flex:${m.parts};background:${this.byId[m.id].mh}"></span>`).join("");
      bar.hidden = !this.mix.length;
    }
    renderRecipe() {
      const T = this.T, P = this.mix.reduce((a, m) => a + m.parts, 0);
      this.$(".tacm-dlg__recipe").innerHTML = this.mix.length ? this.mix.map((m, k) => {
        const c = this.byId[m.id], n = this.name(c);
        return `<li class="tacm-dlg__row"><span class="tacm-dab" style="background:${this.dab(c)}"></span>
          <span class="tacm-dlg__name">${esc(n)}<small>${esc(T.opacity[c.op])}, ${esc(fill(T.share, { p: Math.round(m.parts / P * 100) }))}</small></span>
          <span class="tacm-dlg__step"><button type="button" data-k="${k}" data-d="-1" aria-label="${esc(fill(T.fewer, { name: n }))}" ${m.parts <= 1 ? "disabled" : ""}>&minus;</button>
          <output>${esc(m.parts === 1 ? T.part : fill(T.parts, { n: m.parts }))}</output>
          <button type="button" data-k="${k}" data-d="1" aria-label="${esc(fill(T.more, { name: n }))}" ${m.parts >= MAXP ? "disabled" : ""}>+</button></span>
          <button type="button" class="tacm-dlg__rm" data-rm="${k}" aria-label="${esc(fill(T.remove, { name: n }))}">&times;</button></li>`;
      }).join("") : `<li class="tacm-dlg__empty">${esc(T.empty)}</li>`;
      const sn = this.$(".tacm-dlg__starter"), noteText = this.note || (this.starter ? T.starter_note : "");
      sn.hidden = !noteText; sn.textContent = noteText || "";
      const add = this.$("[data-open-picker]"); add.disabled = this.mix.length >= MAX; add.title = this.mix.length >= MAX ? T.full : "";
      const reset = this.$("[data-reset]");
      if (this.base) { reset.hidden = this.mix.length === 1 && this.mix[0].id === this.base.id; reset.textContent = fill(T.reset, { name: this.name(this.base) }); }
      else { reset.hidden = !this.mix.length; reset.textContent = T.clear_mix; }
    }
    recipeLook(r) {
      if (!r._look) { const s = this.model.summary(r.m.map(([id, parts]) => ({ id, parts }))), b = s.b, w = this.model.white;
        const tint = hexOfR(film(b.K.map((k, i) => 0.5 * k + 0.5 * w.K[i]), 0.5 * b.S + 0.5 * w.s, 1e3, 0));
        r._look = `radial-gradient(circle at 38% 36%, ${s.mass} 0 42%, ${tint} 80%)`; }
      return r._look;
    }
    renderPicker() {
      if (this.sheetMode === "recipes") return this.renderRecipes();
      const T = this.T, q = this.$(".tacm-dlg__find").value.trim().toLowerCase(), full = this.mix.length >= MAX;
      this.$$(".tacm-dlg__filters [data-filter]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.filter === this.filter)));
      const items = this.list.filter(c => (this.filter === "all" || c.g === this.filter) && (!q || this.name(c).toLowerCase().includes(q) || c.n.toLowerCase().includes(q)));
      items.sort((a, b) => GROUP_ORDER.indexOf(a.g) - GROUP_ORDER.indexOf(b.g));
      this.$(".tacm-dlg__grid").innerHTML = items.map(c => {
        const m = this.mix.find(x => x.id === c.id), n = this.name(c);
        return `<div class="tacm-dlg__tile${m ? " is-in" : ""}"><button type="button" class="tacm-dlg__tbtn" data-pick="${c.id}" aria-label="${esc(m ? fill(T.more, { name: n }) : fill(T.add, { name: n }))}" ${full && !m ? "disabled" : ""}>
          <span class="tacm-dab" style="background:${this.dab(c)}"></span><span class="tacm-dlg__tname">${esc(n)}</span></button>${m ? `<span class="tacm-dlg__badge" aria-hidden="true">${m.parts}</span><button type="button" class="tacm-dlg__tminus" data-unpick="${c.id}" aria-label="${esc(m.parts > 1 ? fill(T.fewer, { name: n }) : fill(T.remove, { name: n }))}">&minus;</button>` : ""}</div>`;
      }).join("");
      this.$(".tacm-dlg__note").textContent = !items.length ? fill(T.no_match, { q: this.$(".tacm-dlg__find").value.trim() }) : full ? T.full : "";
    }
    renderRecipes() {
      const T = this.T, q = this.$(".tacm-dlg__find").value.trim().toLowerCase();
      this.$$(".tacm-dlg__filters [data-rfilter]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.rfilter === this.rfilter)));
      const cur = this.mix.map(m => m.id + ":" + m.parts).join(",");
      const rs = this.recipesForLine().filter(r => (this.rfilter === "all" || r.g === this.rfilter) && (!q || (T.rnames[r.id] || "").toLowerCase().includes(q)));
      this.$(".tacm-dlg__grid").innerHTML = rs.map(r => {
        const n = T.rnames[r.id] || r.id, on = r.m.map(([id, p]) => id + ":" + p).join(",") === cur;
        return `<div class="tacm-dlg__tile tacm-dlg__recipe-tile${on ? " is-in" : ""}"><button type="button" class="tacm-dlg__tbtn" data-recipe="${r.id}" aria-pressed="${on}">
          <span class="tacm-dab" style="background:${this.recipeLook(r)}"></span><span class="tacm-dlg__tname">${esc(n)}</span>
          <span class="tacm-dlg__ingr" aria-label="${esc(r.m.map(([id, p]) => (p === 1 ? T.part : fill(T.parts, { n: p })) + " " + this.name(this.byId[id])).join(", "))}">${r.m.map(([id]) => `<i style="background:${this.byId[id].mh}"></i>`).join("")}</span></button></div>`;
      }).join("");
      this.$(".tacm-dlg__note").textContent = rs.length ? "" : fill(T.no_match, { q: this.$(".tacm-dlg__find").value.trim() });
    }
    setPreview(items) {
      this.preview = items ? items.map(m => ({ ...m })) : null;
      this.$(".tacm-dlg__pv").hidden = !this.preview;
      if (this.preview) this.$(".tacm-dlg__pv-text").textContent = fill(this.T.previewing, { name: this.preview.map(m => this.name(this.byId[m.id])).join(" + ") });
      this.renderDrawdown();
    }
    recipeText(items) {
      const T = this.T; if (items.length === 1) return "";
      return items.map(m => `${m.parts === 1 ? T.part : fill(T.parts, { n: m.parts })} ${this.name(this.byId[m.id])}`).join(" + ");
    }
    wheelSvg(slots, base, baseHex) {
      const R = 84, cx = 100, cy = 100, pt = (w, r) => [cx + r * Math.sin(w * Math.PI / 180), cy - r * Math.cos(w * Math.PI / 180)];
      let ring = "";
      for (let k = 0; k < 36; k++) { const w0 = k * 10, w1 = w0 + 10, [x0, y0] = pt(w0, R), [x1, y1] = pt(w1, R), [x2, y2] = pt(w1, R - 16), [x3, y3] = pt(w0, R - 16);
        ring += `<path d="M${x0.toFixed(1)} ${y0.toFixed(1)}A${R} ${R} 0 0 1 ${x1.toFixed(1)} ${y1.toFixed(1)}L${x2.toFixed(1)} ${y2.toFixed(1)}A${R - 16} ${R - 16} 0 0 0 ${x3.toFixed(1)} ${y3.toFixed(1)}Z" fill="${oklchToHex(0.72, 0.14, wheelToOk(w0 + 5))}"/>`; }
      const all = [{ w: base.w }].concat(slots.map(s => ({ w: s.target })));
      const poly = all.map(s => pt(s.w, R - 8).map(v => v.toFixed(1)).join(",")).join(" ");
      const marks = all.map((s, k) => { const [x, y] = pt(s.w, R - 8); return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="7" fill="#fff" stroke="#1a1a1a" stroke-width="2"/><text x="${x.toFixed(1)}" y="${(y + 3.5).toFixed(1)}" text-anchor="middle" font-size="9" font-weight="700" fill="#1a1a1a">${k + 1}</text>`; }).join("");
      return `<svg viewBox="0 0 200 200" aria-hidden="true" class="tacm-dlg__wheel"><circle cx="100" cy="100" r="44" fill="${baseHex}"/>${ring}<polygon points="${poly}" fill="none" stroke="#1a1a1a" stroke-width="1.5" stroke-dasharray="4 3"/>${marks}</svg>`;
    }
    renderHarmony() {
      if (!this.dlg) return;
      const T = this.T, out = this.$(".tacm-dlg__harm"), desc = this.$(".tacm-dlg__desc"), chips = this.$(".tacm-dlg__chips");
      this.$$(".tacm-dlg__chip").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.h === this.harmKind)));
      const teaser = this.$(".tacm-dlg__teaser");
      const hideTeaser = () => { teaser.hidden = true; teaser.innerHTML = ""; };
      if (!this.mix.length) { chips.hidden = false; desc.textContent = T.harmony_intro; out.innerHTML = ""; hideTeaser(); return; }
      const base = this.model.mixIdent(this.mix);
      if (base.C < 0.03) { chips.hidden = true; desc.textContent = T.harmony_neutral; out.innerHTML = ""; hideTeaser(); return; }
      chips.hidden = false;
      if (!this.harmKind) { desc.textContent = T.harmony_intro; out.innerHTML = ""; hideTeaser(); return; }
      desc.innerHTML = `<strong>${esc(T.hnames[this.harmKind])}.</strong> ${esc(T.hdesc[this.harmKind])}`;
      const used = new Set(this.mix.map(m => m.id));
      const slots = HARMONIES[this.harmKind].map(off => { const target = wrap(base.w + off), m = this.model.matchTarget(this.list, target, used); const hk = wheelToOk(target);
        return m && { target, hex: oklchToHex(0.62 + 0.26 * Math.max(0, Math.cos((hk - 100) * Math.PI / 180)), 0.15, hk), ...m }; }).filter(Boolean);
      this.harmSlots = slots;
      const room = MAX - this.mix.length;
      /* upsell on phones: the first suggestions right under the mix */
      if (!isWide() && slots.length) {
        teaser.hidden = false;
        teaser.innerHTML = `<div class="tacm-dlg__teaser-head"><strong>${esc(T.teaser_title)}</strong><span>${esc(T.hnames[this.harmKind])}</span></div>
          <ul>${slots.slice(0, 3).map((s, k) => `<li><span class="tacm-dlg__dabs">${s.items.map(m => `<span class="tacm-dab" style="background:${this.dab(this.byId[m.id])}"></span>`).join("")}</span>
            <span class="tacm-dlg__name">${esc(s.items.map(m => this.name(this.byId[m.id])).join(" + "))}</span>
            <button type="button" data-addmix="${k}" ${s.items.length <= room ? "" : "disabled"}>${esc(T.add_short)}</button></li>`).join("")}</ul>
          <button type="button" class="tacm-dlg__seeharm" data-see-harm>${esc(T.see_harmonies)}</button>`;
      } else hideTeaser();
      if (!isWide() && this.tab !== "harm") return;
      const mixHex = this.model.summary(this.mix).mass;
      const ids = new Set(this.mix.map(m => m.id)); slots.forEach(s => s.items.forEach(m => ids.add(m.id))); this.harmIds = [...ids];
      const row = (k, s) => { const c = this.byId[s.items[0].id], names = s.items.map(m => this.name(this.byId[m.id])).join(" + "), rec = this.recipeText(s.items);
        return `<li class="tacm-dlg__slot"><span class="tacm-dlg__num">${k + 2}</span><span class="tacm-dlg__target" style="background:${s.hex}" title="${esc(T.target)} ${s.hex}"></span>
          <span class="tacm-dlg__dabs">${s.items.map(m => `<span class="tacm-dab" style="background:${this.dab(this.byId[m.id])}"></span>`).join("")}</span>
          <span class="tacm-dlg__name">${esc(names)}<small>${esc(rec || (s.score > 18 ? T.closest : T.opacity[c.op]))}</small></span>
          <span class="tacm-dlg__acts"><button type="button" data-view="${k}">${esc(T.view)}</button><button type="button" data-addmix="${k}" ${s.items.length <= room ? "" : `disabled title="${esc(T.full)}"`}>${esc(T.mix_with)}</button></span></li>`; };
      out.innerHTML = `<div class="tacm-dlg__hgrid">${this.wheelSvg(slots, base, mixHex)}<ol class="tacm-dlg__slots">
          <li class="tacm-dlg__slot is-base"><span class="tacm-dlg__num">1</span><span class="tacm-dlg__target" style="background:${mixHex}"></span><span class="tacm-dlg__dabs">${this.mix.map(m => `<span class="tacm-dab" style="background:${this.dab(this.byId[m.id])}"></span>`).join("")}</span><span class="tacm-dlg__name">${esc(T.harmony_base)}<small>${esc(this.mix.length > 1 ? this.recipeText(this.mix) : this.name(this.byId[this.mix[0].id]))}</small></span></li>
          ${slots.map((s, k) => row(k, s)).join("")}</ol></div>
        <button type="button" class="tacm-dlg__cta2 tacm-dlg__palette-cart">${esc(fill(T.add_palette, { n: ids.size }))}</button><p class="tacm-dlg__pstatus" role="status"></p>`;
    }
    variant(c) {
      const ref = c.L[this.line];
      if (!ref) return Promise.resolve(null);
      if (PREVIEW && PREVIEW.variant) return Promise.resolve(PREVIEW.variant(ref[0], ref[1]));
      if (!this.variantCache.has(c.id)) {
        const url = `${(this.root.dataset.root || "/").replace(/\/?$/, "/")}products/${ref[0]}.js`;
        this.variantCache.set(c.id, fetch(url, { headers: { Accept: "application/json" } }).then(r => r.ok ? r.json() : null).then(p => {
          if (!p || !p.variants || !p.variants.length) return null;
          const v = p.variants.find(x => x.id === ref[1]) || p.variants.slice().sort((a, b) => (a.position || 0) - (b.position || 0))[0];
          return { id: v.id, price: v.price / 100, available: v.available, title: v.public_title || v.title || "" };
        }).catch(() => null));
      }
      return this.variantCache.get(c.id);
    }
    async renderTubes() {
      const T = this.T, token = (this.tubeToken = (this.tubeToken || 0) + 1);
      const rows = await Promise.all(this.mix.map(async m => { const c = this.byId[m.id]; return { c, v: await this.variant(c) }; }));
      if (token !== this.tubeToken) return;
      let total = 0;
      this.$(".tacm-dlg__tubes").innerHTML = rows.map(({ c, v }) => {
        const ok = v && v.available; if (ok) total += v.price;
        return `<div class="tacm-dlg__tube${ok ? "" : " is-out"}"><span class="tacm-dab" style="background:${this.dab(c)}"></span><span class="tacm-dlg__tn">${esc(this.name(c))}${v && v.title ? `<small>${esc(v.title)}</small>` : ""}</span><span>${v ? this.money.format(v.price) : ""}</span></div>`;
      }).join("") || `<p class="tacm-dlg__empty">${esc(T.empty)}</p>`;
      this.$(".tacm-dlg__total strong").textContent = this.money.format(total);
      this.$(".tacm-dlg__cart").disabled = !rows.some(r => r.v && r.v.available);
      this.$(".tacm-dlg__foot").classList.toggle("is-empty", !rows.length);
    }
    async addToCart(ids, btn, status) {
      const T = this.T; btn.disabled = true; status.textContent = T.adding;
      const rows = await Promise.all(ids.map(async id => { const c = this.byId[id]; return { c, v: await this.variant(c) }; }));
      const items = rows.filter(r => r.v && r.v.available).map(r => ({ id: r.v.id, quantity: 1 }));
      const out = rows.filter(r => !(r.v && r.v.available)).map(r => fill(T.unavailable, { name: this.name(r.c) }));
      try {
        if (PREVIEW && PREVIEW.add) await PREVIEW.add(items);
        else {
          const res = await fetch(`${(this.root.dataset.root || "/").replace(/\/?$/, "/")}cart/add.js`, { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items }) });
          if (!res.ok) throw new Error(((await res.json().catch(() => ({}))).description) || "");
        }
        status.textContent = [items.length === 1 && T.added_one ? T.added_one : fill(T.added, { n: items.length })].concat(out).join(" ");
      } catch (e) { status.textContent = e && e.message ? e.message : T.error; }
      btn.disabled = false;
    }
  }

  /* ---------- gallery button ---------- */
  function iconSvg(c) {
    const stops = ["#E4002B", "#F28C00", "#F7C600", "#2DA44E", "#1F6FEB", "#7A3FD9", "#E4002B"];
    return `<span class="tacm-launch__icon" aria-hidden="true" style="background:conic-gradient(${stops.join(",")})"><span style="background:${c.mh}"></span></span>`;
  }
  function addGalleryButton(base, T, open, badge) {
    if (document.querySelector(".tacm-launch")) return;
    const label = document.querySelector(".tali-open"), chips = document.querySelector(".ta-prop-wrap--overlay");
    const host = document.querySelector("[data-tacm-gallery]") || (chips && chips.parentNode) || (label && label.offsetParent);
    if (!host) return;
    const b = document.createElement("button");
    b.type = "button"; b.className = "tacm-launch";
    b.setAttribute("aria-label", T.launch);
    b.innerHTML = `${iconSvg(base)}<span class="tacm-launch__text">${esc(T.launch)}</span>${badge ? `<span class="tacm-launch__badge">${esc(badge)}</span>` : ""}`;
    b.addEventListener("click", e => { e.preventDefault(); e.stopPropagation(); open(); });
    host.appendChild(b);
    /* bottom left of the image; if the handling chips are still on the image, sit just above them */
    const place = () => {
      const ch = host.querySelector(".ta-prop-wrap--overlay");
      if (ch && ch.offsetParent) { const hr = host.getBoundingClientRect(), cr = ch.getBoundingClientRect(); b.style.bottom = Math.round(hr.bottom - cr.top + 8) + "px"; b.style.left = Math.round(cr.left - hr.left) + "px"; }
      else { b.style.bottom = ""; b.style.left = ""; }
    };
    place(); requestAnimationFrame(place); setTimeout(place, 800);
    if ("ResizeObserver" in window) new ResizeObserver(place).observe(host); else window.addEventListener("resize", place);
    if (!reduceMotion()) setTimeout(() => b.classList.add("is-pulse"), 900);
  }

  const DEBUG = () => !!(window.Shopify && window.Shopify.designMode) || /[?&]tacm_debug/.test(location.search);
  function fail(root, msg, err) {
    if (window.console) console.warn("[Tri-Art mixer] " + msg, err || "");
    if (DEBUG()) {
      root.hidden = false;
      const inner = root.querySelector(".tacm__inner") || root;
      if (!root.querySelector(".tacm__debug")) inner.insertAdjacentHTML("afterbegin", '<p class="tacm__debug"></p>');
      root.querySelector(".tacm__debug").textContent = "Mixer: " + msg + (err && err.message ? " (" + err.message + ")" : "");
    } else root.hidden = true;
  }

  async function boot(root) {
    if (root.dataset.tacmReady) return; root.dataset.tacmReady = "1";
    try { await loadSpectral(root.dataset.spectralUrl); }
    catch (e) { return fail(root, "could not load spectral.min.js from " + root.dataset.spectralUrl + ". Check the file is in Assets."); }
    let data;
    try { data = await loadData(root.dataset.dataUrl); }
    catch (e) { return fail(root, "could not load the colour data from " + root.dataset.dataUrl + ". Check ta-colour-mixer-data.json is in Assets with exactly that name.", e); }
    try {
      const T = decodeStrings(root.querySelector(".tacm__strings").textContent);
      if (root.dataset.mode === "page") {
        const model = new Model(data), q = new URLSearchParams(location.search);
        let line = ["hv", "lq", "lv"].includes(q.get("line")) ? q.get("line") : (root.dataset.line || "hv");
        const start = root.dataset.start ? model.find(root.dataset.start) : null;
        if (!q.get("line") && start) line = start.line;
        let mix = null, starter = false;
        if (q.get("mix")) mix = q.get("mix").split(",").map(s => { const [id, n] = s.split(":"); return { id, parts: Math.max(1, Math.min(MAXP, parseInt(n, 10) || 1)) }; }).filter(m => model.byId[m.id]).slice(0, MAX);
        else if (start) mix = [{ id: start.c.id, parts: 1 }];
        else if (root.dataset.starter !== "off") {
          const ok = STARTERS.filter(r => r.every(([id]) => model.byId[id] && model.byId[id].L[line]));
          if (ok.length) { mix = ok[Math.floor(Math.random() * ok.length)].map(([id, parts]) => ({ id, parts })); starter = true; }
        }
        const m = new Mixer(root, model, null, line, T, { inline: root.querySelector(".tacm__app"), mix: mix || [], starter });
        m.open();
        return;
      }
      const model = new Model(data), hit = model.find(root.dataset.handle);
      if (!hit) return fail(root, "the product handle \"" + root.dataset.handle + "\" is not in the colour data file.");
      const { c: base, line } = hit, fr = (root.dataset.lang || "").toLowerCase().startsWith("fr");
      const note = root.querySelector(".tacm__lnote"), ln = T["line_note_" + line];
      if (note && ln) { note.textContent = ln; note.hidden = false; }
      let mixer = null;
      const open = () => { if (!mixer) mixer = new Mixer(root, model, base, line, T); mixer.open(); };
      root.querySelectorAll("[data-tacm-open]").forEach(b => { b.disabled = false; b.addEventListener("click", open); });
      document.querySelectorAll("[data-tacm-launch]").forEach(b => b.addEventListener("click", e => { e.preventDefault(); open(); }));
      addGalleryButton(base, T, open, root.dataset.badge || "");
      const cv = root.querySelector(".tacm__canvas");
      if (cv) {
        const draw = new Drawdown(model, cv, root.querySelector(".tacm__stage"), root.querySelector(".tacm__card"), { tilt: true });
        const show = () => {
          const r = draw.show([{ id: base.id, parts: 1 }]);
          cv.setAttribute("aria-label", fill(T.canvas_label, { name: fr && base.nf ? base.nf : base.n, thick: r.mass }));
          const meta = root.querySelector(".tacm__meta");
          if (meta) meta.innerHTML = fill(esc(T.meta), { thick: `<strong>${r.mass.toUpperCase()}</strong>`, thin: `<strong>${r.thin.toUpperCase()}</strong>`, opacity: esc(T.opacity[r.op]), sheen: esc(T.sheen[r.sh]) });
        };
        if ("IntersectionObserver" in window) { const io = new IntersectionObserver(es => { if (es.some(x => x.isIntersecting)) { io.disconnect(); show(); } }, { rootMargin: "300px 0px" }); io.observe(root); } else show();
        let rT; window.addEventListener("resize", () => { clearTimeout(rT); rT = setTimeout(() => draw.rebuildIfResized(), 250); });
      }
      if (window.console && DEBUG()) console.info("[Tri-Art mixer] ready:", root.dataset.handle, line);
    } catch (e) { fail(root, "failed to start.", e); }
  }
  /* load once the page is idle so the gallery button appears without slowing the page down */
  function schedule(root) {
    const go = () => boot(root);
    if ("requestIdleCallback" in window) requestIdleCallback(go, { timeout: 2500 }); else setTimeout(go, 1200);
  }
  window.TACM = { boot };
  const init = () => document.querySelectorAll(".tacm").forEach(schedule);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
  document.addEventListener("shopify:section:load", e => { const r = e.target.querySelector(".tacm"); if (r) boot(r); });
})();